// Lesson player route. Loads a published lesson and hands it to the
// content-engine LessonPlayer, wiring the API client for attempts + completion.
// docs/replan-v2.md R5.
//
// The player owns flow + feedback; persistence is injected here so the package
// stays free of the app's query client. On exit/finish we invalidate the kid's
// "today" + the parent progress queries so home + dashboard reflect the result.

import { LessonPlayer } from '@fajrarabi/content-engine/react';
import { useT, useLocale } from '@fajrarabi/i18n/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';

import { ApiError } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import { completeLesson, getLesson, submitAttempt, cdnBase } from '~/lib/learn';
import { asLocale } from '~/lib/locale';
import { shellForContext } from '~/lib/roles';

export const Route = createFileRoute('/$locale/lesson/$lessonId')({
  component: LessonRoute,
});

function LessonRoute() {
  const t = useT();
  const { isRtl } = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { locale: rawLocale, lessonId } = useParams({ from: '/$locale/lesson/$lessonId' });
  const locale = asLocale(rawLocale);
  const { currentRoleContext } = useAuth();

  // Learners only. A parent who deep-links here is bounced to their home.
  const isKid = shellForContext(currentRoleContext) === 'kid';
  useEffect(() => {
    if (!isKid) {
      void navigate({ to: '/$locale', params: { locale }, replace: true });
    }
  }, [isKid, navigate, locale]);

  const lessonQuery = useQuery({
    queryKey: ['kids', 'lesson', lessonId],
    queryFn: ({ signal }) => getLesson(lessonId, signal),
    enabled: isKid,
    // Don't retry a paywall (402) or other client errors — show the card at once.
    retry: (count, err) =>
      !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
  });

  function goHome(): void {
    void queryClient.invalidateQueries({ queryKey: ['kids', 'today'] });
    void queryClient.invalidateQueries({ queryKey: ['parent', 'progress'] });
    void navigate({ to: '/$locale', params: { locale } });
  }

  if (!isKid) return null;

  // A 402 means the lesson is behind the paywall — show the upgrade card (CTA to
  // billing) rather than the generic "couldn't load" error.
  const isPaywalled =
    lessonQuery.error instanceof ApiError && lessonQuery.error.code === 'payment_required';

  return (
    <div className="mx-auto max-w-2xl px-4 py-4">
      {lessonQuery.isLoading ? (
        <div className="grid min-h-[60vh] place-items-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-cream-200 border-t-sun-500" />
        </div>
      ) : isPaywalled ? (
        <div className="grid min-h-[60vh] place-items-center text-center">
          <div className="max-w-sm">
            <div className="text-6xl" aria-hidden="true">
              🔒
            </div>
            <h2 className="mt-4 text-2xl font-bold text-ink-900">
              {t('app.paywall.locked_title')}
            </h2>
            <p className="mx-auto mt-2 text-ink-700">{t('app.paywall.locked_body')}</p>
            <button
              type="button"
              onClick={() => navigate({ to: '/$locale/billing', params: { locale } })}
              className="mt-6 inline-flex min-h-[48px] items-center justify-center rounded-full bg-burgundy-500 px-8 font-bold text-cream-50 hover:bg-burgundy-600"
            >
              {t('app.paywall.parent_unlock')}
            </button>
          </div>
        </div>
      ) : lessonQuery.isError || !lessonQuery.data ? (
        <div className="grid min-h-[60vh] place-items-center text-center">
          <div>
            <p className="text-ink-700">{t('content.player.error')}</p>
            <button
              type="button"
              onClick={goHome}
              className="mt-4 inline-flex min-h-[48px] items-center justify-center rounded-full bg-burgundy-500 px-8 font-bold text-cream-50"
            >
              {t('content.player.back_home')}
            </button>
          </div>
        </div>
      ) : (
        <LessonPlayer
          titleKey={lessonQuery.data.titleKey}
          activities={lessonQuery.data.activities}
          t={t}
          tashkeelMode="with"
          cdnBase={cdnBase()}
          isRtl={isRtl}
          onSubmitAttempt={submitAttempt}
          onComplete={() => completeLesson(lessonId)}
          onExit={goHome}
        />
      )}
    </div>
  );
}
